/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { UnitUtility } from '@bmsp/bmsp.web.component.common';
import {
  KPI_VALUE_DECIMAL_PRECISION,
  MAGIC_VALUE,
  euiMessage,
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  UtilityType
} from '../Constants';
import { filterNullValue } from '../EnergyUsagesViewDetailsUtils';
import { handleEuiUnitConversion } from '../unitHandling/EuiUnitHandling';
import { measurementTypes, responseUnitMapping } from './MeasurementTypeInfo';
import { ConversionType, IunitObject } from './UnitModel';
import { IAny } from '../../../DashboardKPIWidgets/IEnergyManagementProps';

/**
 * To convert single value from one unit to other unit
 * If toUnit is provided convert to that else first calculate to which unit to convert and convert accordingly
 */

export const UnitSymbols: any = {
  GJ: 'gigajoule',
  MJ: 'megajoule',
  KJ: 'kilojoule',
  kJ: 'kilojoule',
  TJ: 'terajoule',
  J: 'joules',
  Wh: 'watt hour',
  kW: 'kilowatt',
  W: 'watt',
  mW: 'megawatt',
  MW: 'megawatt',
  gW: 'gigawatt',
  GW: 'gigawatt',
  KWh: 'kilowatt hour',
  kWh: 'kilowatt hour',
  MWh: 'megawatt hour',
  GWh: 'gigawatt hour',
  TWh: 'terawatt hour',
  thm: 'therm',
  Dth: 'decatherms',
  'K Dth': 'kilo decatherms',
  MMBTU: 'millionbtu',
  'K MMBTU': 'kilo millionbtu',
  kVA: 'kilovolt ampere',
  MVA: 'megavolt ampere',
  GVA: 'gigavolt ampere',
  TVA: 'teravolt ampere',
  VA: 'volt ampere',
  kVARH: 'kilovolt-ampere reactive hour',
  gal: 'gallon',
  kGal: 'kilo gallon',
  MGal: 'mega gallon',
  GGal: 'giga gallon'
};

export const getConvertedValueForUtilitySummary = (
  conversionType: string,
  fromUnitName: string,
  value: number | null,
  toUnitSymbol?: IAny
) => {
  if (value === null) {
    return {
      Value: null,
      Unit: fromUnitName
    };
  }

  // if (
  //   conversionType === ConversionType?.ELECTRICITY ||
  //   conversionType.toLowerCase() === UtilityType.Electric ||
  //   ((conversionType === ConversionType?.GAS ||
  //     conversionType.toLowerCase() === UtilityType.Gas) &&
  //     UnitSymbols[toUnitSymbol] &&
  //     UnitSymbols[toUnitSymbol].includes('joule') &&
  //     (fromUnitName !== 'mega_joules"))
  // ) {
  //   // bmsp convertValue is unable to convert millionbtu to any unit, so it is converted to btu and then made to convert
  //   // 1 million btu = 1,000,000 btu. This needs to be checked from the bmsp code
  //   const modifiedValue: any = UnitUtility.convertValue(
  //     conversionType.toLowerCase() === 'gas' ? 'btu' : fromUnitName,
  //     toUnitSymbol
  //       ? UnitSymbols[toUnitSymbol]
  //         ? UnitSymbols[toUnitSymbol]
  //         : toUnitSymbol
  //       : fromUnitName,
  //     conversionType.toLowerCase() === 'gas' ? Number(value) * 1000000 : value
  //   );
  //   // const convertedValue = getConvertedValueFromUnit(
  //   //   conversionType,
  //   //   fromUnitName,
  //   //   value,
  //   //   toUnitSymbol
  //   // );

  //   return {
  //     Value: parseFloat(modifiedValue?.toFixed(KPI_VALUE_DECIMAL_PRECISION)),
  //     Unit: toUnitSymbol
  //   };
  // }

  const convertedValue = getConvertedValueFromUnit(
    conversionType,
    fromUnitName,
    value,
    toUnitSymbol
  );
  return {
    Value: convertedValue.Value,
    Unit: convertedValue.Unit
  };
};

export const getConvertedValue = (
  conversionType: string,
  fromUnitName: string,
  value: number | null,
  toUnitSymbol?: IAny
) => {
  if (value === null) {
    return {
      Value: null,
      Unit: fromUnitName
    };
  }

  if (
    conversionType === ConversionType?.ELECTRICITY ||
    conversionType.toLowerCase() === UtilityType.Electric ||
    ((conversionType === ConversionType?.GAS ||
      conversionType.toLowerCase() === UtilityType.Gas) &&
      UnitSymbols[toUnitSymbol] &&
      UnitSymbols[toUnitSymbol].includes('joule') &&
      !fromUnitName.includes('joule'))
  ) {
    // bmsp convertValue is unable to convert millionbtu to any unit, so it is converted to btu and then made to convert
    // 1 million btu = 1,000,000 btu. This needs to be checked from the bmsp code
    const modifiedValue: any = UnitUtility.convertValue(
      conversionType.toLowerCase() === 'gas' ? 'btu' : fromUnitName,
      toUnitSymbol
        ? UnitSymbols[toUnitSymbol]
          ? UnitSymbols[toUnitSymbol]
          : toUnitSymbol
        : fromUnitName,
      conversionType.toLowerCase() === 'gas' ? Number(value) * 1000000 : value
    );
    // const convertedValue = getConvertedValueFromUnit(
    //   conversionType,
    //   fromUnitName,
    //   value,
    //   toUnitSymbol
    // );

    return {
      Value: parseFloat(modifiedValue?.toFixed(KPI_VALUE_DECIMAL_PRECISION)),
      Unit: toUnitSymbol
    };
  }

  const convertedValue = getConvertedValueFromUnit(
    conversionType,
    fromUnitName,
    value,
    toUnitSymbol
  );
  return {
    Value: convertedValue.Value,
    Unit: convertedValue.Unit
  };
};

//This function is added so that to fix "Cannot read properties of null (reading 'toFixed')"
export const getConvertedNullValue = (
  totalEnergyDeviationPercentage: number | null
) => {
  if (totalEnergyDeviationPercentage === null) {
    return null;
  } else {
    return parseFloat(
      totalEnergyDeviationPercentage?.toFixed(KPI_VALUE_DECIMAL_PRECISION)
    );
  }
};

/**
 * Takes array of values, finds highest value and convert that from one unit to other unit
 * If toUnit is provided convert to that else first calculate to which unit to convert and convert accordingly
 */

export const getMaxConvertedValueFromArrayForUtilitySummary = (
  conversionType: string,
  fromUnitName: string,
  values: Array<number | null>,
  originalUnit?: string
) => {
  let maxValue;
  let convertedValues: IAny;
  // if (
  //   conversionType === ConversionType.ELECTRICITY ||
  //   (conversionType === ConversionType.GAS && fromUnitName === 'megajoule')
  // ) {
  //   convertedValues = [
  //     ...values.map((val: any) =>
  //       UnitUtility.convertValue(
  //         originalUnit
  //           ? originalUnit === 'million btu' || originalUnit === 'millionbtu'
  //             ? 'btu'
  //             : originalUnit
  //           : fromUnitName,
  //         fromUnitName === 'million btu' || fromUnitName === 'millionbtu'
  //           ? 'btu'
  //           : fromUnitName,
  //         conversionType === ConversionType.GAS &&
  //           originalUnit === 'million btu'
  //           ? Number(val) * 1000000
  //           : val
  //       )
  //     )
  //   ];
  // } else {

  // }

  convertedValues = values;

  const filterArray = filterNullValue(convertedValues);
  maxValue = filterArray.length > 0 ? Math.max(...filterArray) : 0;

  const convertedValue = getConvertedValueFromUnit(
    conversionType,
    fromUnitName,
    maxValue
  );
  return {
    Value: convertedValue.Value,
    Unit: convertedValue.Unit
  };
};

export const getMaxConvertedValueFromArray = (
  conversionType: string,
  fromUnitName: string,
  values: Array<number | null>,
  originalUnit?: string
) => {
  let maxValue;
  let convertedValues: IAny;
  if (
    conversionType === ConversionType.ELECTRICITY ||
    (conversionType === ConversionType.GAS &&
      fromUnitName === 'megajoule' &&
      !originalUnit?.includes('joule'))
  ) {
    convertedValues = [
      ...values.map((val: any) =>
        UnitUtility.convertValue(
          originalUnit
            ? originalUnit === 'million btu' || originalUnit === 'millionbtu'
              ? 'btu'
              : originalUnit
            : fromUnitName,
          fromUnitName === 'million btu' || fromUnitName === 'millionbtu'
            ? 'btu'
            : fromUnitName,
          conversionType === ConversionType.GAS &&
            originalUnit === 'million btu'
            ? Number(val) * 1000000
            : val
        )
      )
    ];
  } else {
    convertedValues = values;
  }

  const filterArray = filterNullValue(convertedValues);
  maxValue = filterArray?.length > 0 ? Math.max(...filterArray) : 0;

  const convertedValue = getConvertedValueFromUnit(
    conversionType,
    fromUnitName,
    maxValue
  );
  return {
    Value: convertedValue.Value,
    Unit: convertedValue.Unit
  };
};

export const getMeasuremtTypeObj = (conversionType: string) => {
  return measurementTypes.find((type) =>
    type.measurementType.includes(conversionType.toLowerCase())
  );
};

export const getUnitObject = (conversionType: string, unitTypeKey: string) => {
  let unitObject: Array<IunitObject> = [];
  const measurementTypeObj = getMeasuremtTypeObj(conversionType);
  if (measurementTypeObj) {
    unitObject = measurementTypeObj.units[unitTypeKey];
  }
  return unitObject;
};

export const getFullNameFromSymbol = (
  unitObject: Array<IunitObject>,
  unitSymbol: string
) => {
  let fullName: string = '';
  if (unitObject && unitSymbol) {
    const unitObj = unitObject.find(
      (unit) =>
        unit.symbol.toLowerCase() === unitSymbol.toLowerCase() ||
        unit.displayUnit === unitSymbol
    );
    if (unitObj) {
      fullName = unitObj.name;
    }
  }
  return fullName;
};

export const getUnitObjectFromValue = (
  unitObject: Array<IunitObject>,
  value: number
) => {
  if (unitObject && value >= 0) {
    return unitObject.find(
      (unit) => unit.minValue <= value && unit.maxValue >= value
    );
  }
};

export const convertValue = (
  unitObject: Array<IunitObject>,
  value: number,
  fromUnitName: string,
  toUnitName: string
) => {
  let convertedValue = 0;
  //Value is provided in smallest unit
  if (fromUnitName == toUnitName) {
    return value;
  }

  if (value > 0) {
    let toUnitScale = unitObject.find((unit: IunitObject) => {
      return unit.name == toUnitName;
    })?.minValue;
    toUnitScale = toUnitScale === 0 ? 1 : toUnitScale;

    let fromUnitScale = unitObject.find((unit: IunitObject) => {
      return unit.name == fromUnitName;
    })?.minValue;
    fromUnitScale = fromUnitScale === 0 ? 1 : fromUnitScale;

    if (toUnitScale && fromUnitScale && fromUnitScale > 0) {
      const scaleFactor = toUnitScale / fromUnitScale;
      if (scaleFactor && scaleFactor > 0) {
        convertedValue = value / scaleFactor;
      }
    }
  }
  return convertedValue;
};

const getConvertedValueFromUnit = (
  conversionType: string,
  fromUnitName: string,
  value: number,
  toUnitSymbol?: string
) => {
  try {
    let absoluteValue = Math.abs(value);
    let finalValue, smallestUnit, unitObj;

    const unitTypeKey = responseUnitMapping[fromUnitName.toString()];
    const unitObject = getUnitObject(conversionType, unitTypeKey);
    const emptyUnitObj = {
      name: '',
      symbol: '',
      minValue: 0,
      maxValue: 0,
      displayUnit: ''
    };

    if (unitObject) {
      unitObj = unitObject;
      smallestUnit = unitObject[0];
    } else {
      unitObj = [emptyUnitObj];
      smallestUnit = emptyUnitObj;
    }

    // convert received value in respective smallest unit
    const valueInSmallestUnit = convertValue(
      unitObject,
      absoluteValue,
      fromUnitName,
      smallestUnit.name
    );

    let finalConversionUnitObj = getFinalUnitConversionObject(
      unitObj,
      valueInSmallestUnit,
      toUnitSymbol
    );

    if (finalConversionUnitObj) {
      finalValue = convertValue(
        unitObject,
        valueInSmallestUnit,
        smallestUnit.name,
        finalConversionUnitObj.name
      );

      return {
        Value: getFinalValue(value, finalValue),
        Unit: finalConversionUnitObj.displayUnit
      };
    }
    return { Value: value, Unit: fromUnitName };
  } catch (exception) {
    return { Value: value, Unit: fromUnitName };
  }
};

export const getFinalUnitConversionObject = (
  unitObject: Array<IunitObject>,
  valueInSmallestUnit: number,
  toUnitSymbol?: string
) => {
  // If toUnit is provided, then convert fromvalue (in smallest unit) to Tounit, else find toUnit by range in which valueInSmallestUnit lies
  if (!toUnitSymbol) {
    return getUnitObjectFromValue(unitObject, valueInSmallestUnit);
  } else {
    const toUnitName = getFullNameFromSymbol(unitObject, toUnitSymbol);
    return unitObject.find((unit) => unit.name == toUnitName);
  }
};

const getFinalValue = (actualValue: number | null, convertedValue: number) => {
  convertedValue = parseFloat(
    convertedValue.toFixed(KPI_VALUE_DECIMAL_PRECISION)
  );
  if (actualValue !== null && actualValue < 0) {
    return -convertedValue;
  }
  return convertedValue;
};

const getUnitSymbol = (unitObject: IunitObject[], unitName: string) => {
  let symbol: string = '';
  if (unitObject && unitName) {
    const unitObj = unitObject.find(
      (unit) =>
        unit.name.toLowerCase() === unitName.toLowerCase() ||
        unit.displayUnit === unitName
    );
    if (unitObj) {
      symbol = unitObj.displayUnit;
    }
  }
  return symbol;
};

export const getSymbolFromFullName = (
  fromUnitName: string,
  conversionType: string
) => {
  const unitTypeKey = responseUnitMapping[fromUnitName.toString()];
  const unitObject = getUnitObject(conversionType, unitTypeKey);
  return getUnitSymbol(unitObject, fromUnitName);
};

export const handleNumberUpscaling = (
  unScaledValue: number | string,
  preferredUnit: string,
  totalFootPrintNativeUnit?: string
) => {
  let parsedValue = 0;
  let value: IAny = UnitUtility.convertValue(
    totalFootPrintNativeUnit ? totalFootPrintNativeUnit : 'square foot',
    preferredUnit,
    Number(unScaledValue)
  );
  if (typeof value === 'string') {
    if (!isNaN(parseInt(value, MAGIC_VALUE.TEN))) {
      parsedValue = parseInt(value, MAGIC_VALUE.TEN);
    } else {
      return '';
    }
  } else {
    parsedValue = value;
  }
  if (parsedValue) {
    if (parsedValue > MAGIC_VALUE.MILLION) {
      return (
        parseFloat(
          (parsedValue / MAGIC_VALUE.MILLION).toFixed(MAGIC_VALUE.TWO)
        ) + 'm'
      );
    } else if (parsedValue > MAGIC_VALUE.THOUSAND) {
      return (
        parseFloat(
          (parsedValue / MAGIC_VALUE.THOUSAND).toFixed(MAGIC_VALUE.TWO)
        ) + 'K'
      );
    } else {
      return parsedValue.toFixed(MAGIC_VALUE.TWO);
    }
  }
  return value;
};

export const IMSEmaaSPortfolioSiteTablePropsCheck = (
  message: string | undefined,
  euiValue: number,
  unit: string,
  preferredEuiUnit: string
) => {
  if (message === euiMessage.SUCCESS) {
    return parseFloat(
      handleEuiUnitConversion(euiValue, unit, preferredEuiUnit).toFixed(
        KPI_VALUE_DECIMAL_PRECISION
      )
    );
  } else {
    return null;
  }
};

export const IMSEmaaSPDeviationPercentageCheck = (
  message: string | undefined,
  euiDeviationPercentage: number
) => {
  if (message === euiMessage.SUCCESS) {
    return parseFloat(
      euiDeviationPercentage.toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
    );
  } else {
    return null;
  }
};

export const IMSEmaaSMessageCheck = (message: string | undefined) => {
  if (message === euiMessage.SUCCESS) {
    return '';
  } else {
    return message;
  }
};
